import React, { useState, useEffect } from 'react';
import { FaCopy, FaCheckCircle, FaArrowLeft, FaMicrosoft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuthContext } from '../../../../context/AuthContext';

const OutlookCalendarIntegration: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [calendarUrl, setCalendarUrl] = useState<string>('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (user?.id) {
      const baseUrl = window.location.origin;
      const feedUrl = `${baseUrl}/api/calendar/feed/${user.id}/shifts.ics`;
      setCalendarUrl(feedUrl);
    }
  }, [user]);

  const handleCopyUrl = () => {
    navigator.clipboard.writeText(calendarUrl);
    setCopied(true);
    toast.success('Kalender-URL kopiert!');
    setTimeout(() => setCopied(false), 2000);
  };

  const containerStyle: React.CSSProperties = {
    padding: 24,
    maxWidth: 900,
  };

  const headerStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginBottom: 32,
  };

  const backButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '8px 16px',
    background: '#f5f5f5',
    border: 'none',
    borderRadius: 8,
    fontSize: 14,
    fontWeight: 600,
    color: '#0f2b5a',
    cursor: 'pointer',
    transition: 'background 0.2s',
  };

  const logoStyle: React.CSSProperties = {
    width: 64,
    height: 64,
    borderRadius: 12,
    background: '#0078D4',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
  };

  const cardStyle: React.CSSProperties = {
    background: '#fff',
    border: '1px solid #e0e3ef',
    borderRadius: 12,
    padding: 24,
    marginBottom: 20,
  };

  const urlBoxStyle: React.CSSProperties = {
    display: 'flex',
    gap: 12,
    padding: 16,
    background: '#f8f9fa',
    border: '1px solid #e0e3ef',
    borderRadius: 8,
    marginTop: 16,
  };

  const urlInputStyle: React.CSSProperties = {
    flex: 1,
    padding: '10px 12px',
    border: '1px solid #d0d4e4',
    borderRadius: 6,
    fontSize: 13,
    fontFamily: 'monospace',
    background: '#fff',
  };

  const copyButtonStyle: React.CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: copied ? '#4caf50' : '#0066cc',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background 0.2s',
    whiteSpace: 'nowrap',
  };

  const stepStyle: React.CSSProperties = {
    display: 'flex',
    gap: 16,
    marginBottom: 20,
  };

  const stepNumberStyle: React.CSSProperties = {
    width: 32,
    height: 32,
    borderRadius: '50%',
    background: '#0078D4',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 14,
    fontWeight: 700,
    flexShrink: 0,
  };

  const tabStyle: React.CSSProperties = {
    marginTop: 24,
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <button
          style={backButtonStyle}
          onClick={() => navigate('/settings/integrations')}
          onMouseEnter={(e) => (e.currentTarget.style.background = '#e8e8e8')}
          onMouseLeave={(e) => (e.currentTarget.style.background = '#f5f5f5')}
        >
          <FaArrowLeft /> Zurück
        </button>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 32 }}>
        <div style={logoStyle}>
          <FaMicrosoft size={32} />
        </div>
        <div>
          <h1 style={{ fontSize: 24, margin: 0, marginBottom: 8, color: '#0f2b5a' }}>
            Outlook Calendar Integration
          </h1>
          <p style={{ fontSize: 14, color: '#666', margin: 0 }}>
            Synchronisieren Sie Ihre Schichten automatisch mit Outlook Calendar
          </p>
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={{ fontSize: 18, marginBottom: 16, color: '#0f2b5a' }}>
          Ihre persönliche Kalender-URL
        </h2>
        <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, marginBottom: 16 }}>
          Nutzen Sie diese URL, um Ihre Schichten in Outlook Calendar zu abonnieren. Der Kalender
          wird automatisch aktualisiert, wenn neue Schichten hinzugefügt oder geändert werden.
        </p>

        <div style={urlBoxStyle}>
          <input
            type="text"
            value={calendarUrl}
            readOnly
            style={urlInputStyle}
          />
          <button
            style={copyButtonStyle}
            onClick={handleCopyUrl}
            onMouseEnter={(e) => {
              if (!copied) e.currentTarget.style.background = '#0052a3';
            }}
            onMouseLeave={(e) => {
              if (!copied) e.currentTarget.style.background = '#0066cc';
            }}
          >
            {copied ? <FaCheckCircle /> : <FaCopy />}
            {copied ? 'Kopiert!' : 'Kopieren'}
          </button>
        </div>
      </div>

      <div style={cardStyle}>
        <h2 style={{ fontSize: 18, marginBottom: 20, color: '#0f2b5a' }}>
          Anleitung: Kalender in Outlook hinzufügen
        </h2>

        <div style={tabStyle}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#0078D4' }}>
            📱 Outlook Web (outlook.com / outlook.office.com)
          </h3>

          <div style={stepStyle}>
            <div style={stepNumberStyle}>1</div>
            <div>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#0f2b5a' }}>
                URL kopieren
              </h4>
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: 0 }}>
                Kopieren Sie die Kalender-URL oben mit dem "Kopieren"-Button.
              </p>
            </div>
          </div>

          <div style={stepStyle}>
            <div style={stepNumberStyle}>2</div>
            <div>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#0f2b5a' }}>
                Outlook öffnen
              </h4>
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: 0 }}>
                Öffnen Sie{' '}
                <a
                  href="https://outlook.office.com/calendar"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#0066cc', textDecoration: 'none', fontWeight: 600 }}
                >
                  outlook.office.com/calendar
                </a>{' '}
                in einem neuen Tab.
              </p>
            </div>
          </div>

          <div style={stepStyle}>
            <div style={stepNumberStyle}>3</div>
            <div>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#0f2b5a' }}>
                Kalender hinzufügen
              </h4>
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: 0 }}>
                Klicken Sie auf <strong>"Kalender hinzufügen"</strong> {">"}{' '}
                <strong>"Aus dem Internet abonnieren"</strong>.
              </p>
            </div>
          </div>

          <div style={stepStyle}>
            <div style={stepNumberStyle}>4</div>
            <div>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#0f2b5a' }}>
                URL einfügen
              </h4>
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: 0 }}>
                Fügen Sie die kopierte URL ein, geben Sie einen Namen ein (z.B. "Meine Schichten")
                und klicken Sie auf <strong>"Importieren"</strong>.
              </p>
            </div>
          </div>
        </div>

        <div style={{ ...tabStyle, borderTop: '1px solid #e0e3ef', paddingTop: 24 }}>
          <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 16, color: '#0078D4' }}>
            💻 Outlook Desktop (Windows/Mac)
          </h3>

          <div style={stepStyle}>
            <div style={stepNumberStyle}>1</div>
            <div>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#0f2b5a' }}>
                URL kopieren
              </h4>
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: 0 }}>
                Kopieren Sie die Kalender-URL oben.
              </p>
            </div>
          </div>

          <div style={stepStyle}>
            <div style={stepNumberStyle}>2</div>
            <div>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#0f2b5a' }}>
                Kalender öffnen
              </h4>
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: 0 }}>
                Öffnen Sie Outlook und wechseln Sie zur Kalender-Ansicht.
              </p>
            </div>
          </div>

          <div style={stepStyle}>
            <div style={stepNumberStyle}>3</div>
            <div>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#0f2b5a' }}>
                Internetkalender hinzufügen
              </h4>
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: 0 }}>
                Klicken Sie auf <strong>"Datei"</strong> {">"}{' '}
                <strong>"Kontoeinstellungen"</strong> {">"}{' '}
                <strong>"Internetkalender"</strong> {">"} <strong>"Neu"</strong>.
              </p>
            </div>
          </div>

          <div style={stepStyle}>
            <div style={stepNumberStyle}>4</div>
            <div>
              <h4 style={{ fontSize: 15, fontWeight: 600, marginBottom: 8, color: '#0f2b5a' }}>
                URL einfügen und bestätigen
              </h4>
              <p style={{ fontSize: 14, color: '#666', lineHeight: 1.6, margin: 0 }}>
                Fügen Sie die URL ein und klicken Sie auf <strong>"Hinzufügen"</strong>. Geben Sie
                einen Namen ein und bestätigen Sie.
              </p>
            </div>
          </div>
        </div>

        <div style={{ marginTop: 24, padding: 16, background: '#f0f7ff', borderRadius: 8 }}>
          <p style={{ fontSize: 14, color: '#0f2b5a', lineHeight: 1.6, margin: 0 }}>
            <strong>✓ Fertig!</strong> Ihre Schichten werden jetzt automatisch in Outlook Calendar
            angezeigt und synchronisiert.
          </p>
        </div>
      </div>

      <div
        style={{
          ...cardStyle,
          background: '#fff3e0',
          border: '1px solid #ffb74d',
        }}
      >
        <h3 style={{ fontSize: 16, fontWeight: 600, marginBottom: 12, color: '#e65100' }}>
          🔒 Sicherheitshinweis
        </h3>
        <p style={{ fontSize: 14, color: '#e65100', lineHeight: 1.6, margin: 0 }}>
          Diese URL ist persönlich und sollte nicht mit anderen geteilt werden. Jeder mit dieser
          URL kann Ihre Schichten sehen. Wenn Sie vermuten, dass die URL kompromittiert wurde,
          wenden Sie sich bitte an Ihren Administrator.
        </p>
      </div>
    </div>
  );
};

export default OutlookCalendarIntegration;
